// ⚠️ AVTOMATIK KO'CHIRILGAN NUSXA — QO'L BILAN TAHRIRLAMANG.
// Manba: frontend/shared/format/money.ts
// Yangilash: panel papkasidan `node ../shared/sync.mjs`
// Qorovul: `npm run test:format` (uchala panelda) — nusxa manbadan
// bir bayt farq qilsa yiqiladi.
// Pul — MINOR birlikdagi butun son + valyuta kodi.
//
// NEGA YALANG'OCH `number` EMAS (07-FRONTEND.md §7):
// `number` o'zi qaysi valyuta va qaysi birlik ekanini aytmaydi. "50000" —
// bu 50 000 so'mmi yoki 500.00 dollarmi? Ikki xil javob bir kodda uchrasa,
// xato faqat foydalanuvchi hisobida ko'rinadi. `Money` bu savolni tipda hal
// qiladi: funksiya `Money` so'rasa, chaqiruvchi valyutani yozishga majbur.
//
// Backend `long` da, minor birlikda beradi (`02-LEDGER.md` §11: `double`
// taqiqlangan, tiyin/pul — butun son). Frontend ham shu birlikni saqlaydi va
// aylantirishni FAQAT ko'rsatish va kiritish chegarasida qiladi.

import { currentIntlLocale } from '@/app/i18n'

import { numberFormat } from './format.intl'
import { currencySymbol, formatMoney, getCurrencyCode } from './format.money'

export interface Money {
  /** MINOR birlikdagi butun son: 5000000 = 50 000.00 UZS */
  readonly amount: number
  /** ISO 4217 kodi, masalan "UZS" */
  readonly currency: string
}

/**
 * ISO 4217 da kasr xonalari 2 dan farq qiladigan valyutalar.
 * Ro'yxatda yo'q valyuta uchun 2 olinadi — bu ISO'dagi eng keng tarqalgan holat.
 */
const EXPONENTS: Readonly<Record<string, number>> = {
  // Kasrsiz — minor birlik = major birlik
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  // Uch xonali
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
}

/** Valyutaning kasr xonalari soni (minor birliklar darajasi) */
export function currencyExponent(currency: string): number {
  return EXPONENTS[currency.trim().toUpperCase()] ?? 2
}

/** Minor → major: 5000000 UZS → 50000 */
export function minorToMajor(amount: number, currency: string): number {
  return amount / 10 ** currencyExponent(currency)
}

/**
 * Major → minor: 50000 UZS → 5000000.
 *
 * `Math.round` MAJBURIY: 19.99 * 100 JavaScript'da 1998.9999999999998 beradi.
 * Yaxlitlamasdan bu son butun deb serverga ketsa, bir tiyin yo'qoladi — va
 * ledger invarianti (`SUM(txn) = 0`) buziladi.
 *
 * ⚠️ `Math.round` yarimni YUQORIGA suradi (-0.5 → -0), ya'ni manfiy
 * summada u `Intl` ning `halfExpand` idan farq qiladi. Bu yerda muhim emas:
 * forma qiymati minor birlikdagi butun songa aylantiriladi va shundan keyin
 * hech qanday yaxlitlash qolmaydi.
 */
export function majorToMinor(amount: number, currency: string): number {
  return Math.round(amount * 10 ** currencyExponent(currency))
}

/** Backenddan kelgan minor summadan `Money` yasaydi */
export function money(amount: number, currency: string): Money {
  return { amount, currency: currency.trim().toUpperCase() }
}

/** Major birlikdagi son (masalan formadan) → `Money` */
export function moneyFromMajor(amount: number, currency: string): Money {
  const code = currency.trim().toUpperCase()
  return { amount: majorToMinor(amount, code), currency: code }
}

export function isZero(value: Money): boolean {
  return value.amount === 0
}

export function isNegative(value: Money): boolean {
  return value.amount < 0
}

/**
 * Ikki summani qo'shish. Valyutalar mos kelmasa — XATO, jimgina qo'shilmaydi.
 *
 * Konvertatsiya bu yerda ATAYLAB yo'q: kurs vaqt bo'yicha o'zgaradi va
 * qaysi kursda hisoblangani saqlanishi kerak (`02-LEDGER.md` §11.2 — bu
 * alohida `txn`). Frontendda jimgina konvertatsiya qilish esa hisobotni
 * takrorlab bo'lmaydigan qiladi.
 */
export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) {
    throw new Error(`Turli valyutalarni qo'shib bo'lmaydi: ${a.currency} va ${b.currency}`)
  }
  return { amount: a.amount + b.amount, currency: a.currency }
}

export function negateMoney(value: Money): Money {
  return { amount: -value.amount, currency: value.currency }
}

/**
 * `Money` ni ko'rsatish uchun matnga: { amount: 5000000, currency: 'UZS' }
 * → uz "50 000 so'm" · ru "50 000 сум" · en "50,000 UZS"
 *
 * ⚠️ YAXLITLASH QOIDASI — BU YERDA HECH QACHON YAXLITLANMAYDI.
 * Kirish minor birlikdagi butun son, chiqish esa aynan shu sonning
 * o'nlik nuqta qo'yilgan ko'rinishi:
 *
 *   · `exponent === 0` (JPY, UGX ...) — minor = major, kasr yo'q
 *   · major butun songa teng (5000000 UZS → 50000) — kasrsiz chiqadi
 *   · qolgan holatda kasr AYNAN `exponent` xonagacha ko'rsatiladi,
 *     ya'ni minor birlikdagi butun sonning hamma raqami saqlanadi
 *     (1234 USD → "12.34", 1230 USD → "12.3")
 *
 * `minimumFractionDigits: 0` — so'm va afg'onida tiyin amalda
 * ishlatilmaydi va "50 000,00 so'm" ortiqcha shovqin; dollarda esa 12.34
 * ni 12 deb ko'rsatish YOLG'ON bo'lardi. Ikkala holat shu bitta qoida
 * bilan to'g'ri chiqadi.
 *
 * ⚠️ `formatMoney` (major, `maximumFractionDigits: 0`) bu yerdan FAQAT
 * kasrsiz shoxda chaqiriladi — ya'ni uning yaxlitlashi bu yo'lda
 * hech qachon ishga tushmaydi.
 */
export function formatMoneyValue(value: Money | null | undefined): string {
  if (!value || Number.isNaN(value.amount)) return '—'

  const exponent = currencyExponent(value.currency)
  const major = minorToMajor(value.amount, value.currency)

  // Butun summa — MVP bilan aynan bir xil chiqadi (kasrsiz)
  if (exponent === 0 || Number.isInteger(major)) {
    return formatMoney(major, value.currency)
  }

  // Kasr bor — `formatMoney` uni tashlab yuborardi, shuning uchun bu yerda
  // alohida formatlanadi. Locale AYNAN o'sha manbadan olinadi (brauzerning
  // standart tili emas) — aks holda panel o'zbekchada bo'lib, son inglizcha
  // ajratgichlar bilan chiqardi.
  const locale = currentIntlLocale()
  const text = numberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: exponent,
  }).format(major)
  return `${text} ${currencySymbol(value.currency, locale)}`
}

/** Tenantning standart valyutasidagi bo'sh summa — forma boshlang'ich qiymati uchun */
export function zeroMoney(currency: string = getCurrencyCode()): Money {
  return { amount: 0, currency: currency.trim().toUpperCase() }
}
